import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Stack,
  Typography,
} from "@mui/material";
import { Component, ErrorInfo, ReactNode } from "react";

// Source: https://reactjs.org/docs/error-boundaries.html
// NOTE: Only class components can be error boundaries

interface Props {
  children: ReactNode;
  fallbackComponent?: ReactNode;
}

interface State {
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {};

  public static getDerivedStateFromError(error: Error): State {
    return {
      error: error,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Error Boundary:", error, errorInfo);
    this.setState((st) => {
      return {
        ...st,
        error,
        errorInfo,
      };
    });
  }

  public render() {
    if (this.state.error) {
      if (this.props.fallbackComponent) {
        return this.props.fallbackComponent;
      } else {
        return (
          <Box
            sx={{
              minHeight: "100vh",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Container maxWidth="sm">
              <Card>
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Stack spacing={3} alignItems="center" textAlign="center">
                    <Typography variant="h4">Something went wrong</Typography>
                    <Typography color="text.secondary">
                      Refresh the app or sign out to reset your session.
                    </Typography>
                    <Stack direction="row" spacing={1.5}>
                      <Button
                        onClick={() => {
                          window.location.reload();
                        }}
                      >
                        Refresh
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={() => {
                          window.location.href = "/logout";
                        }}
                      >
                        Logout
                      </Button>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Container>
          </Box>
        );
      }
    } else {
      return this.props.children;
    }
  }
}

export default ErrorBoundary;
